import React, { useEffect, useRef, useState } from "react";
import assamGeoJSON from "../data/assamGeoJSON.json";
import { HC_NAME_MAP } from "../data/mapData";
import { fetchDistrictAvailability } from "../data/api";
import DistrictStoreModal from "./DistrictStoreModal";

/* global Highcharts */

export default function AssamMap({ mode = "avail" }) {
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const chartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [apiDistricts, setApiDistricts] = useState([]);

  useEffect(() => {
    fetchDistrictAvailability().then(r => setApiDistricts(r.data)).catch(console.error);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      wrapperRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFsChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
      setTimeout(() => { if (chartRef.current) chartRef.current.reflow(); }, 150);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  useEffect(() => {
    if (!containerRef.current || typeof Highcharts === "undefined" || !apiDistricts.length) return;

    const colorFn = (d) => {
      const v = parseFloat(d.avail);
      if (mode === "avail") return v >= 90 ? "#34d399" : v >= 75 ? "#fbbf24" : "#f87171";
      if (mode === "stockout") {
        const so = parseInt(d.stockout_stores) || 0;
        return so === 0 ? "#6ee7b7" : so <= 1 ? "#a7f3d0" : so <= 3 ? "#fde68a" : so <= 5 ? "#fdba74" : "#f87171";
      }
      // For expiry/expired modes, use availability as proxy since we don't have expiry data
      return v >= 90 ? "#6ee7b7" : v >= 80 ? "#a7f3d0" : v >= 70 ? "#fde68a" : v >= 60 ? "#fdba74" : "#f87171";
    };

    const mapData = apiDistricts.map((d) => ({
      name: HC_NAME_MAP[d.n] || d.n,
      value: parseFloat(d.avail),
      color: colorFn(d),
      distName: d.n,
      distApiName: d.n,
      avail: parseFloat(d.avail),
      totalStores: d.total_stores,
      storesAbove90: d.stores_above_90,
      stockoutStores: d.stockout_stores,
    }));

    const tooltipFormatter = function () {
      const p = this;
      let rows = "";
      if (mode === "avail" || mode === "expiry" || mode === "expired") {
        rows =
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">Availability</td><td style="font-weight:700;color:#34d399;text-align:right">' + p.avail + '%</td></tr>' +
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">Total Stores</td><td style="font-weight:700;text-align:right">' + (p.totalStores || '-') + '</td></tr>' +
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">Stores &ge; 90%</td><td style="font-weight:700;color:#34d399;text-align:right">' + (p.storesAbove90 || '0') + '</td></tr>';
      } else if (mode === "stockout") {
        rows =
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">Stockout Stores</td><td style="font-weight:700;color:#f87171;text-align:right">' + (p.stockoutStores || '0') + '</td></tr>' +
          '<tr><td style="padding:3px 10px 3px 0;color:#6b7c93;font-size:11px">Availability</td><td style="font-weight:700;color:#34d399;text-align:right">' + p.avail + '%</td></tr>';
      }
      return (
        '<div style="padding:8px 4px;min-width:180px"><div style="font-weight:700;font-size:13px;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #e8ecf1">' +
        (p.distName || p.name) +
        "</div><table style=\"border-collapse:collapse;width:100%\">" + rows + "</table>" +
        '<div style="margin-top:8px;padding-top:6px;border-top:1px solid #e8ecf1;font-size:10px;color:#3b82f6;font-weight:600;text-align:center">Click to view store details &rarr;</div>' +
        "</div>"
      );
    };

    chartRef.current = Highcharts.mapChart(containerRef.current, {
      chart: {
        map: assamGeoJSON,
        backgroundColor: "transparent",
        margin: [4, 4, 4, 4],
        animation: { duration: 400 },
      },
      title: { text: "" },
      credits: { enabled: false },
      mapNavigation: { enabled: false },
      colorAxis: { visible: false },
      legend: { enabled: false },
      tooltip: {
        useHTML: true,
        backgroundColor: "#ffffff",
        borderColor: "#e8ecf1",
        borderRadius: 8,
        shadow: { offsetX: 0, offsetY: 4, opacity: 0.06, width: 12 },
        style: { fontSize: "12px", color: "#1e293b", padding: "0" },
        headerFormat: "",
        pointFormatter: tooltipFormatter,
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: function () {
                const apiName = this.distApiName || this.distName || this.name;
                setSelectedDistrict(apiName);
              }
            }
          }
        }
      },
      series: [
        {
          data: mapData,
          joinBy: ["NAME", "name"],
          name: "Districts",
          borderWidth: 1.5,
          borderColor: "#ffffff",
          nullColor: "#e8ecf1",
          states: {
            hover: { borderColor: "#475569", borderWidth: 2, brightness: 0.03 },
          },
          dataLabels: {
            enabled: true,
            format: "{point.properties.NAME}",
            style: {
              fontSize: "7.5px",
              fontWeight: "600",
              color: "#334155",
              textOutline: "2px white",
              fontFamily: "'Segoe UI', system-ui, sans-serif",
            },
          },
        },
      ],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [mode, apiDistricts]);

  return (
    <>
      <div
        ref={wrapperRef}
        style={{
          position: "relative",
          height: isFullscreen ? "100vh" : "340px",
          width: "100%",
          background: isFullscreen ? "#fff" : "transparent",
        }}
      >
        <button
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit Fullscreen" : "View Fullscreen"}
          style={{
            position: "absolute", top: 8, right: 8, zIndex: 10,
            width: 32, height: 32, borderRadius: 6,
            border: "1px solid var(--border)", background: "var(--navy-light)",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", fontSize: ".75rem",
            boxShadow: "0 2px 6px rgba(0,0,0,.15)", transition: "background .2s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = "var(--navy)")}
          onMouseOut={(e) => (e.currentTarget.style.background = "var(--navy-light)")}
        >
          <i className={isFullscreen ? "fas fa-compress" : "fas fa-expand"}></i>
        </button>

        <div
          ref={containerRef}
          style={{
            height: isFullscreen ? "100vh" : "340px",
            width: "100%",
            transition: "height 0.3s ease",
          }}
        />
      </div>

      {selectedDistrict && (
        <DistrictStoreModal
          district={selectedDistrict}
          mode={mode}
          onClose={() => setSelectedDistrict(null)}
        />
      )}
    </>
  );
}
